<script setup lang="ts">
    import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
    import EmptyState from '@shared/components/EmptyState.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import Loading from '@shared/components/Loading.vue';
    import Preview from '@shared/components/Preview.vue';
    import List from '@shared/components/List/List.vue';
    import ListItem from '@views/Surveys/ListItem.vue';
    import { type SurveyInList } from '@views/Surveys/SurveysTypes.d';
    import { vTooltip } from '@shared/directives';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { addDefaultSurvey, disconnectEventSurveys, loadEventSurveys, publishEventSurvey, removeEventSensitiveAnswers } from '@views/Event/Event.access';
    import { type Event, type EventSurveySummary, EventStatusNames } from '@views/Event/EventTypes';
    import { eventKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const publicHost = computed(() => contextAccess.state.currentAccount!.PublicEventUrl.PublicHost);

    const state = reactive<{
        surveys: EventSurveySummary[];
        selectedIds: string[];
        isLoaded: boolean;
        isAdding: boolean;
        isDisconnecting: boolean;
    }>({
        surveys: [],
        selectedIds: [],
        isLoaded: false,
        isAdding: false,
        isDisconnecting: false,
    });

    const previewUrl = ref<string | null>(null);

    const isActive = computed(() => event.value?.Status === EventStatusNames.Active);
    const defaultSurveyId = computed(() => event.value?.DefaultSurveyId ?? null);

    async function loadSurveys() {
        try {
            state.surveys = await loadEventSurveys(props.eventId);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
        } finally {
            state.isLoaded = true;
        }
    }

    onMounted(loadSurveys);

    async function setAsDefaultSurvey(surveyId: string) {
        if (!(await saveEventProperty('DefaultSurveyId', surveyId, $Resources.DefaultLabel))) return;
        if (!isActive.value) return;
        try {
            await publishEventSurvey(props.eventId);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
        }
    }

    // Parity with legacy EditEventViewModel.setDefaultSurvey: a single connected form is always the default.
    watch(
        () => [state.surveys, defaultSurveyId.value] as const,
        ([surveys, defaultId]) => {
            if (surveys.length !== 1 || !event.value) return;
            if (surveys[0].SurveyId !== defaultId) saveEventProperty('DefaultSurveyId', surveys[0].SurveyId, $Resources.DefaultLabel);
        }
    );

    // The default form can't be disconnected (parity with the legacy pane).
    const disabledCheckboxes = computed(() => (defaultSurveyId.value ? [defaultSurveyId.value] : []));

    // Adapts the event survey summary to the shape the shared survey ListItem renders.
    const listItems = computed(() =>
        state.surveys.map(
            (survey) =>
                ({
                    ...survey,
                    SurveyName: survey.InternalName,
                    LastModifiedBy: { Name: survey.LastModifiedBy },
                    Event: null,
                    Summary: survey,
                }) as unknown as SurveyInList & { Summary: EventSurveySummary }
        )
    );

    async function disconnectSurveys() {
        state.isDisconnecting = true;
        try {
            await disconnectEventSurveys(props.eventId, state.selectedIds);
            state.surveys = state.surveys.filter((s) => !state.selectedIds.includes(s.SurveyId));
            state.selectedIds = [];
            bsAlert();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isDisconnecting = false;
        }
    }

    async function addDefaultSignUpForm() {
        state.isAdding = true;
        try {
            const surveyId = await addDefaultSurvey(props.eventId, !!event.value?.RequireAttestation);
            if (!defaultSurveyId.value) await setAsDefaultSurvey(surveyId);
            await loadSurveys();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
        } finally {
            state.isAdding = false;
        }
    }

    async function deleteSensitiveAnswers(survey: EventSurveySummary) {
        try {
            await removeEventSensitiveAnswers(props.eventId, survey.SurveyId);
            bsAlert({ type: AlertTypes.Success, title: $Resources.EventSurveyRemoveAnswersHeading, content: $Resources.EventSurveyRemoveAnswersInfoText });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
        }
    }

    const publicSurveyUrl = (survey: EventSurveySummary) => combineUrlSegments(publicHost.value, 'Surveys', survey.SurveyId);
    const filterKeys = ['Title', 'SurveyName', 'LastModifiedBy.Name'];

    const orderOptions = [
        { display: $Resources.LastModifiedLabel, key: 'LastModified' },
        { display: $Resources.SurveysSortByTitleLabel, key: 'Title' },
        { display: $Resources.EventDashboardResponseHeader, key: 'ResponseCount' },
        { display: $Resources.EventDashboardRespondentsHeader, key: 'RespondentCount' },
    ];

    const newSurveyRoute = computed(() => ({ name: 'SurveysNew', query: { eventId: props.eventId } }));
</script>

<template>
    <h2 class="h3 m-0-t">{{ $Resources.EventNavEntryForm }}</h2>

    <Loading v-if="!state.isLoaded" loadingStyle="progress-bar" show fixed />

    <EmptyState v-else-if="state.surveys.length === 0" class="m-lg-b" :title="$Resources.EventMissingFormHeading" :body="$Resources.EventMissingFormText">
        <template #actions>
            <div class="form-group m-sm-b">
                <button type="button" class="btn btn-info" :class="{ 'is-busy': state.isAdding }" :disabled="state.isAdding" @click="addDefaultSignUpForm">
                    {{ $Resources.EventMissingFormUseStandardBtnText }}
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
            <div class="form-group m-sm-b">
                <RouterLink class="btn btn-subtle" :to="newSurveyRoute">
                    {{ $Resources.EventMissingFormUseCustomBtnText }}
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                </RouterLink>
            </div>
        </template>
    </EmptyState>

    <template v-else>
        <List
            itemId="SurveyId"
            :items="listItems"
            :checkedItems="state.selectedIds"
            :disabledCheckboxes="disabledCheckboxes"
            :disableCheckboxes="!canEdit"
            :filterKeys="filterKeys"
            :orderOptions="orderOptions"
            orderByValue="LastModified"
            orderByDirection="desc"
            hideHeader
            hideDelete
            @update:checkedItems="(newVal: (string | number)[]) => (state.selectedIds = newVal as string[])"
            @clearSelected="state.selectedIds = []">
            <template #toolbar-selected>
                <ConfirmButton
                    v-if="state.selectedIds.length > 0"
                    button-classes="btn-warning"
                    :button-text="`${$Resources.EventRemoveIssueFromEventBtnText} (${state.selectedIds.length})`"
                    :disabled="!canEdit || state.isDisconnecting"
                    undo-button
                    @confirm="disconnectSurveys" />
            </template>
            <template #item="{ item }">
                <ListItem :survey="item" showStats @showPreview="(id: string) => (previewUrl = combineUrlSegments(publicHost, 'Surveys', id, '?preview=true'))">
                    <template #heading-actions>
                        <small v-if="defaultSurveyId === item.SurveyId" class="m-xs-r">
                            <span class="glyphicon glyphicon-star"></span>
                            {{ $Resources.DefaultLabel }}
                        </small>

                        <button v-else type="button" class="btn btn-default btn-xs" :disabled="!canEdit" @click="setAsDefaultSurvey(item.SurveyId)">
                            {{ $Resources.EventSetIssueAsDefaultBtnText }}
                        </button>

                        <!-- ConfirmButton has a fragment root, so the tooltip lives on a wrapper -->
                        <span v-if="item.HasSensitiveQuestions && item.ResponseCount > 0" v-tooltip.top="$Resources.EventSurveyRemoveAnswersInfoText">
                            <ConfirmButton
                                button-classes="btn-default btn-xs"
                                :button-text="$Resources.EventSurveyRemoveAnswersHeading"
                                :disabled="!canEdit"
                                danger
                                undo-button
                                @confirm="deleteSensitiveAnswers(item.Summary)" />
                        </span>
                    </template>
                    <template #details>
                        <p v-if="isActive">
                            {{ $Resources.SurveyUrlLabel }}:
                            <a :href="publicSurveyUrl(item.Summary)" target="_blank" rel="noopener">
                                {{ publicSurveyUrl(item.Summary) }}
                                <span class="glyphicon glyphicon-new-window small c-light m-xs-l" aria-hidden="true"></span>
                            </a>
                        </p>
                    </template>
                </ListItem>
            </template>
        </List>

        <BsAlert
            :type="AlertTypes.Info"
            :title="$Resources.EventCreateMoreSignUpFormsHeading"
            :content="$Resources.EventCreateMoreSignUpFormsText"
            :dismissible="false">
            <template #button>
                <div class="d-flex gap-xs flex-wrap w-full">
                    <button
                        type="button"
                        class="btn btn-info btn-sm"
                        :class="{ 'is-busy': state.isAdding }"
                        :disabled="!canEdit || state.isAdding"
                        @click="addDefaultSignUpForm">
                        {{ $Resources.EventMissingFormUseStandardBtnText }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </button>

                    <RouterLink class="btn btn-subtle btn-sm" :class="{ disabled: !canEdit }" :to="newSurveyRoute">
                        {{ $Resources.EventMissingFormUseCustomBtnText }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </RouterLink>
                </div>
            </template>
        </BsAlert>
    </template>

    <Preview :preview-url="previewUrl" @close="previewUrl = null" />
</template>
